<template>
  <div class="financial-tab--financial-trade-references-tab">

    <b-card v-if='!buttonsHeaderVisible' class="buttonHeader">
      <b-row>
        <b-button
          class="cancelButton"
          type="button"
          @click="onCancel()"
          size="sm"
        >Cancel</b-button>
        <b-button
          type="button"
          variant="primary"
          @click="onSubmit()"
          size="sm"
          :disabled="isSaving"
        ><i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save</b-button>
      </b-row>
    </b-card>

    <div class="form-row">
      <b-col cols="5">
        <div class="form-group">
          <form>
            <b-card bg-variant="light">
              <b-form-group
                label="Select a reference to edit or add a new one"
                label-class="pt-0 text-dark"
                class="mb-0"
              >
                <div id="buttonHeader" class="buttons-panel">
                  <b-button
                    type="button"
                    @click="onCancel()"
                    size="sm"
                  >Cancel</b-button>
                  <b-button
                    type="button"
                    variant="primary"
                    @click="onSubmit()"
                    size="sm"
                    :disabled="isSaving"
                  ><i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save</b-button>
                </div>
                <!-- NAME -->
                <div class="form-row">
                  <b-col cols="8">
                    <div class="form-group">
                        <b-form-group class="required" label="Company">
                        <input
                          type="text"
                          class="form-control"
                          id="name-input"
                          v-model="tradeReference.name"
                          :class="{ 'is-invalid': $v.tradeReference.name.$error }"
                          @blur="$v.tradeReference.name.$touch()"
                        >
                        <div class="invalid-feedback" v-if="$v.tradeReference.name.$error">
                          <div v-if="!$v.tradeReference.name.required">Company is required</div>
                        </div>
                      </b-form-group>
                    </div>
                  </b-col>

                  <b-col cols="4">
                    <required-descriptor />
                  </b-col>
                </div>

                <!-- CONTACT -->
                <div class="form-row">
                  <b-col cols="8">
                    <div class="form-group">
                      <b-form-group class="required" label="Contact">
                        <input
                          type="text"
                          class="form-control"
                          id="contact-input"
                          v-model="tradeReference.contact"
                          :class="{ 'is-invalid': $v.tradeReference.contact.$error }"
                          @blur="$v.tradeReference.contact.$touch()"
                        >
                        <div class="invalid-feedback" v-if="$v.tradeReference.contact.$error">
                          <div v-if="!$v.tradeReference.contact.required">Contact is required</div>
                        </div>
                      </b-form-group>
                    </div>
                  </b-col>
                </div>

                <div class="form-row">
                  <!-- TERMS -->
                  <b-col cols="4">
                    <div class="form-group">
                      <b-form-group class="required" label="Terms">
                        <input
                          type="text"
                          class="form-control"
                          id="terms-input"
                          v-model="tradeReference.terms"
                          :class="{ 'is-invalid': $v.tradeReference.terms.$error }"
                          @blur="$v.tradeReference.terms.$touch()"
                        >
                        <div class="invalid-feedback" v-if="$v.tradeReference.terms.$error">
                          <div v-if="!$v.tradeReference.terms.required">Terms is required</div>
                        </div>
                      </b-form-group>
                    </div>
                  </b-col>

                  <!-- CREDIT LINES -->
                  <b-col cols="4">
                    <div class="form-group">
                      <b-form-group class="required" label="Credit Line">
                        <input
                          type="text"
                          class="form-control"
                          id="credit-lines-input"
                          v-model="tradeReference.creditLine"
                          :class="{ 'is-invalid': $v.tradeReference.creditLine.$error }"
                          @blur="$v.tradeReference.creditLine.$touch()"
                        >
                        <div
                          class="invalid-feedback"
                          v-if="$v.tradeReference.creditLine.$error"
                        >
                          <div v-if="!$v.tradeReference.creditLine.required">Credit Line is required</div>
                          <div v-if="!$v.tradeReference.creditLine.decimal">Credit Line is invalid</div>
                        </div>
                      </b-form-group>
                    </div>
                  </b-col>
                </div>

                <div class="form-row">
                  <b-col cols="8">
                    <div class="form-group">
                      <b-form-group label="Comment" class="form-input-group">
                        <b-textarea
                          class="form-control comment-textarea"
                          id="comment-input"
                          v-model="tradeReference.comment"
                        ></b-textarea>
                      </b-form-group>
                    </div>
                  </b-col>
                </div>
              </b-form-group>
            </b-card>
          </form>
        </div>
      </b-col>
      <b-col cols="5" offset="1">
        <b-row class="search">
          <b-col cols="4">
            <b-form-input
              size="sm"
              class="mr-sm-2 search-input"
              placeholder="Search"
              left
              @input="onSearch"
              v-model="searchQuery"
            ></b-form-input>
          </b-col>
        </b-row>

        <references-grid
          :items="tradeReferencesList"
          :onEdit="onEdit"
          :onDelete="onDelete"
        />
      </b-col>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { debounce } from 'lodash';
import { required, decimal } from 'vuelidate/lib/validators';
import { createScroll, destroyScroll, isElementInView } from '@/shared/scroll'

import financialService from '../../service';
import financialTradeReferencesFields from './fields';
import ReferencesGrid from './components/ReferencesGrid';

const SEARCH_DEBOUNCE_TIME = 750;

export default {
  name: 'FinancialTradeReferencesTab',
  components: {
    ReferencesGrid,
  },
  data() {
    return {
      companyId: '',
      isSaving:false,
      tradeReference: {
        id: 0,
        accountId: this.companyId,
        name: '',
        contact: '',
        terms: '',
        creditLine: '',
      },

      fields: financialTradeReferencesFields,
      searchQuery: '',
      buttonsHeaderVisible: true,
    };
  },
  validations: {
    tradeReference: {
      name: { required },
      contact: { required },
      terms: { required },
      creditLine: { required, decimal },
    },
  },
  computed: {
    ...mapState('financial', ['tradeReferencesList']),
  },
  methods: {
    ...mapActions('financial', ['getAccountTradeReferenceList', 'search']),
    clearForm() {
      this.tradeReference = {
        id: 0,
        accountId: this.companyId,
        name: '',
        contact: '',
        terms: '',
        creditLine: '',
      };
      this.$v.$reset();
    },
    onSearch: debounce(function() {
      this.searchQuery = this.searchQuery.replace(/#/g, '');
      let payload = {
        accountId: this.companyId,
        searchQuery: this.searchQuery,
      };
      this.search(payload);
    }, SEARCH_DEBOUNCE_TIME),
    onSubmit: function() {
      this.$v.tradeReference.$touch();
      if (this.$v.tradeReference.$invalid) {
        return;
      }
      try {
        this.isSaving = true;
        if (this.tradeReference.id === 0) {
          financialService
            .addAccountTradeReference(this.tradeReference)
            .then(() => {
              let payload = {
                accountId: this.companyId,
                searchQuery: this.searchQuery,
              };
              this.isSaving = false;
              this.getAccountTradeReferenceList(payload);
              this.clearForm();
              return;
            });
        } else {
          financialService
            .updateAccountTradeReference(this.tradeReference)
            .then(() => {
              let payload = {
                accountId: this.companyId,
                searchQuery: this.searchQuery,
              };
              this.isSaving = false;
              this.getAccountTradeReferenceList(payload);
              this.clearForm();
              return;
            });
        }
      }catch (e) {
        this.isSaving = false;
        /*console.log(e.message);*/
      }
    },
    onCancel() {
      this.clearForm();
      return;
    },
    onEdit(item) {
      financialService.getAccountTradeReferenceById(item.id).then(result => {
        this.tradeReference = result;
      });
    },
    async onDelete(item) {
      const deleteConfirmed = await this.openConfirmBox({message: `Are you sure you want to delete "${item.name}"?`});
      if (deleteConfirmed) {
        financialService
          .deleteAccountTradeReference(item.id)
          .then(() => {
            let payload = {
              accountId: this.companyId,
              searchQuery: this.searchQuery,
            };
            this.clearForm();
            this.getAccountTradeReferenceList(payload);
          });
      }
    },
    handleScroll () {
      this.buttonsHeaderVisible = isElementInView(document.getElementById('buttonHeader'));
    },
  },
  created() {
    this.companyId = this.$route.params.id;
    this.tradeReference.accountId = this.companyId;
    createScroll(this);
  },
  mounted() {
    let payload = {
      accountId: this.companyId,
      searchQuery: this.searchQuery,
    };
    this.getAccountTradeReferenceList(payload);
  },
  destroyed() {
    destroyScroll(this);
  },
};
</script>

<style lang="scss">
.financial-tab--financial-trade-references-tab {
  .buttons-panel {
    text-align: right;
  }
  .search-input {
    margin-bottom: 5px;
  }
  .buttons-panel {
    text-align: right;
    .btn {
      margin-left: 10px;
    }
  }
  .buttonHeader {
    position: fixed;
    top: 15%;
    left: 45%;
    z-index: 7;
  }
  .cancelButton {
    margin-right: 10px;
  }
}
</style>
